import { useEffect, useRef, useState } from 'react';

/**
 * Hook to detect if the virtual keyboard is visible on mobile devices.
 * Uses the Visual Viewport API and focus tracking to detect keyboard.
 * Works on both iOS and Android.
 *
 * Returns:
 * - isKeyboardVisible: Whether keyboard is currently open
 * - isReady: Whether hook has initialized
 */
const useKeyboardVisible = () => {
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const initialWindowHeight = useRef<number>(0);
  const focusedElement = useRef<HTMLElement | null>(null);
  const lastKeyboardHeight = useRef<number>(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Store initial window height
    initialWindowHeight.current = window.innerHeight;

    // Delay ready state to avoid initial jump
    const initTimer = setTimeout(() => {
      setIsReady(true);
    }, 100);

    const viewport = window.visualViewport;

    const calculateOffset = () => {
      if (!focusedElement.current) {
        if (lastKeyboardHeight.current !== 0) {
          lastKeyboardHeight.current = 0;
          setIsKeyboardVisible(false);
        }
        return;
      }

      if (!viewport) return;

      // Calculate keyboard height from visual viewport
      const offsetTop = viewport.offsetTop;
      const keyboardHeight = window.innerHeight - viewport.height - Math.max(0, offsetTop);

      // Only update state if keyboard height actually changed
      if (keyboardHeight > 150) {
        if (lastKeyboardHeight.current !== keyboardHeight) {
          lastKeyboardHeight.current = keyboardHeight;
          setIsKeyboardVisible(true);
        }
      } else {
        if (lastKeyboardHeight.current !== 0) {
          lastKeyboardHeight.current = 0;
          setIsKeyboardVisible(false);
        }
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const tagName = target.tagName?.toUpperCase();
      const isTextInput = tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable;

      // Check if input type should trigger keyboard
      const inputType = (target as HTMLInputElement).type?.toLowerCase();
      const nonKeyboardTypes = ['checkbox', 'radio', 'submit', 'button', 'reset', 'file', 'hidden', 'image'];
      const shouldTriggerKeyboard = isTextInput && !nonKeyboardTypes.includes(inputType);

      if (shouldTriggerKeyboard) {
        focusedElement.current = target;
        // Small delay to let keyboard open
        setTimeout(calculateOffset, 300);
      }
    };

    const handleFocusOut = () => {
      // Delay to check if focus moved to another input
      setTimeout(() => {
        const activeElement = document.activeElement as HTMLElement;
        const tagName = activeElement?.tagName?.toUpperCase();
        const isStillInInput = tagName === 'INPUT' || tagName === 'TEXTAREA' || activeElement?.isContentEditable;

        if (!isStillInInput) {
          focusedElement.current = null;
          if (lastKeyboardHeight.current !== 0) {
            lastKeyboardHeight.current = 0;
            setIsKeyboardVisible(false);
          }
        }
      }, 100);
    };

    const handleViewportChange = () => {
      if (focusedElement.current) {
        calculateOffset();
      }
    };

    const handleResize = () => {
      // Update initial height if window grows (orientation change)
      if (window.innerHeight > initialWindowHeight.current && !focusedElement.current) {
        initialWindowHeight.current = window.innerHeight;
      }
      if (focusedElement.current) {
        calculateOffset();
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    window.addEventListener('resize', handleResize);

    if (viewport) {
      viewport.addEventListener('resize', handleViewportChange);
      viewport.addEventListener('scroll', handleViewportChange);
    }

    return () => {
      clearTimeout(initTimer);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('resize', handleResize);
      if (viewport) {
        viewport.removeEventListener('resize', handleViewportChange);
        viewport.removeEventListener('scroll', handleViewportChange);
      }
    };
  }, []);

  return { isKeyboardVisible, isReady };
};

export default useKeyboardVisible;
